import React, { useState, useRef } from 'react';
import { X, UploadCloud } from 'lucide-react';
import { Input } from './Input';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onUpload: (files: File[], title: string) => void;
  accept?: string;
  multiple?: boolean;
}

export const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, title, onUpload, accept = "*", multiple = false }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileTitle, setFileTitle] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
      if (!fileTitle && e.target.files.length > 0) {
        setFileTitle(e.target.files[0].name.split('.')[0]);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles, fileTitle);
      setSelectedFiles([]);
      setFileTitle('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título / Descrição</label>
            <Input 
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="Ex: Documento Importante"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo</label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
            >
               <input 
                 type="file" 
                 accept={accept} 
                 multiple={multiple}
                 ref={fileInputRef} 
                 onChange={handleFileChange} 
                 className="hidden" 
               />
               <UploadCloud size={28} className="text-gray-400 mb-2" />
               {selectedFiles.length > 0 ? (
                 <span className="text-sm font-medium text-indigo-600">
                   {selectedFiles.length} arquivo(s) selecionado(s)
                 </span>
               ) : (
                 <>
                   <span className="text-sm font-medium text-gray-900">Clique para selecionar</span>
                   <span className="text-xs text-gray-500 mt-1">Formatos suportados: {accept}</span>
                 </>
               )}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={selectedFiles.length === 0} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              Salvar e Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
